import { useSnackbar } from '@app/hooks/useSnackbar';
import { useWhitelabelConfigLogoDeleteMutation } from '@dieterApi/whitelabel/useWhitelabelConfigLogoDeleteMutation';
import { useWhitelabelConfigLogoUploadMutation } from '@dieterApi/whitelabel/useWhitelabelConfigLogoUploadMutation';
import { t } from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../Button/Button';

interface Props {
  currentLogoB64: string | null;
  variant?: 'DEFAULT' | 'LIGHT' | 'AVATAR';
  previewBackground?: string; // optional override background for preview panel
  label?: string; // optional custom label
}

export const LogoUpload = ({ currentLogoB64, variant = 'DEFAULT', previewBackground, label }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [uploadCompanyLogo, { loading: uploading }] = useWhitelabelConfigLogoUploadMutation();
  const [deleteCompanyLogo, { loading: deleting }] = useWhitelabelConfigLogoDeleteMutation();
  const [deleteCompanyLogoNoRefetch, { loading: deletingSilent }] = useWhitelabelConfigLogoDeleteMutation(false);

  // revoke object URL to prevent leaks
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const makeBlankFile = () => {
    const base64Data =
      'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAABhGlDQ1BJQ0MgcHJvZmlsZQAAKJF9kT1Iw0AcxV9bpVIqDlZQcchQneyiUhxrFYpQIdQKrTqYXPoFTRqSFBdHwbXg4Mdi1cHFWVcHV0EQ/ABxdnBSdJES/5cUWsR4cNyPd/ced+8Af7PKVLMnAaiaZWRSSSGXXxWCrwhhCGGMIC4xU58TxTQ8x9c9fHy9i/Es73N/jn6lYDLAJxAnmG5YxBvE8U1L57xPHGFlSSE+J5406ILEj1yXXX7jXHLYzzMjRjYzTxwhFkpdLHcxKxsq8QxxVFE1yvfnXFY4b3FWq3XWvid/YbigrSxzneYYUljEEkQIkFFHBVVYiNGqkWIiQ/tJD/+o4xfJJZOrAkaOBdSgQnL84H/wu1uzOD3lJoWTQO+LbX+MA8FdoNWw7e9j226dAIFn4Err+GtNYPaT9EZHix4BA9vAxXVHk/eAyx1g+EmXDMmRAjT9xSLwfkbflAcGb4HQmttbex+nD0CWukrfAAeHwESJstc93t3X3du/Z9r9/QDObXLLp4f6dgAAAAZiS0dEAPwA0gAM8zyrVgAAAAlwSFlzAAAN1wAADdcBQiibeAAAAAd0SU1FB+gKFQ0HHEgs1ZgAAAALSURBVAjXY2AAAgAABQAB4iYFmwAAAABJRU5ErkJggg==';
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    return new File([new Blob([byteArray], { type: 'image/png' })], 'blank.png', { type: 'image/png' });
  };

  const setSelectedFile = (f: File | null) => {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    setFile(f);
    if (f) setObjectUrl(URL.createObjectURL(f));
    else setObjectUrl(null);
  };

  const handlePick = () => inputRef.current?.click();

  const onFiles = (files: FileList | null) => {
    const f = files?.[0];
    if (!f) return;
    const maxSize = 5 * 1024 * 1024;
    if (f.size > maxSize) {
      enqueueSnackbar(t('route.whitelabel.logoSizeError', 'File size exceeds 5MB.'), { variant: 'error' });
      return;
    }
    setSelectedFile(f);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => onFiles(e.target.files);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    onFiles(e.dataTransfer.files);
  };
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const background = previewBackground || (variant === 'DEFAULT' ? 'var(--color-primary, #ffffff)' : '#ffffff');

  const handleSave = async () => {
    if (!file) return;
    try {
      if (currentLogoB64) await deleteCompanyLogoNoRefetch({ variables: { variant } });
      await uploadCompanyLogo({ variables: { file, size: file.size, variant } });
      setSelectedFile(null);
      enqueueSnackbar(t('common.saved', 'Saved'), { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(t('route.whitelabel.logoUploadError', 'Error uploading logo.'), { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCompanyLogo({ variables: { variant } });
      enqueueSnackbar(t('common.deleted', 'Deleted'), { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(t('route.whitelabel.logoDeleteError', 'Error deleting logo.'), { variant: 'error' });
    }
  };

  const handleSetBlank = () => {
    setSelectedFile(makeBlankFile());
  };

  const handleCancel = () => setSelectedFile(null);

  const busy = uploading || deleting || deletingSilent;

  return (
    <div className="bg-white p-5 rounded-lg border shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {label || t('route.whitelabel.currentLogo', 'Current Logo')}
          {variant === 'LIGHT' ? ' (Light)' : ''}
        </h3>
        {currentLogoB64 && !file && (
          <button
            type="button"
            className="text-xs text-red-500 hover:underline disabled:opacity-50"
            disabled={busy}
            onClick={handleDelete}
          >
            {variant === 'LIGHT'
              ? t('route.whitelabel.deleteLight', 'Delete Light Logo')
              : t('route.whitelabel.delete', 'Delete Custom Logo')}
          </button>
        )}
      </div>

      <div
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        className={
          'relative group rounded-md border-2 border-dashed flex flex-col items-center justify-center text-center p-4 transition-colors ' +
          (dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-400')
        }
        style={{ background }}
      >
        <input
          ref={inputRef}
          id={`logo-upload-${variant}`}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleInputChange}
        />
        {/* Preview */}
        {objectUrl ? (
          <img
            src={objectUrl}
            alt={t('route.whitelabel.preview', 'Preview') || 'Preview'}
            className="max-h-28 max-w-full object-contain mb-2"
          />
        ) : currentLogoB64 ? (
          <img
            src={currentLogoB64}
            alt={t('route.whitelabel.currentLogo', 'Current Logo') || 'Logo'}
            className="max-h-28 max-w-full object-contain mb-2 opacity-90 group-hover:opacity-100 transition"
          />
        ) : (
          <div className="text-gray-500 text-xs mb-2">{t('route.whitelabel.noLogo', 'No logo')}</div>
        )}

        {/* Instructions / CTA */}
        {!file && (
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={handlePick}
              disabled={busy}
              className="text-xs font-medium text-primary-600 hover:text-primary-700 underline disabled:opacity-50"
            >
              {variant === 'LIGHT'
                ? t('route.whitelabel.uploadLightLabel', 'Upload light logo (for white backgrounds)')
                : t('route.whitelabel.uploadLabel', 'Upload a new logo')}
            </button>
            <div className="text-[10px] text-gray-500">
              {t('common.or', 'or')} {''}
              <span>{t('common.drag_drop', 'drag & drop')}</span>
            </div>
          </div>
        )}

        {file && (
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center gap-3 p-4 rounded-md">
            <p className="text-white text-xs truncate max-w-[160px]" title={file.name}>
              {file.name}
            </p>
            <div className="flex gap-2">
              <Button onClick={handleSave} spinner={uploading} disabled={busy} className="text-xs px-3 py-1 h-auto">
                {t('common.action.save', 'Save')}
              </Button>
              <Button onClick={handleCancel} disabled={busy} className="text-xs px-3 py-1 h-auto btn-secondary">
                {t('common.action.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleSetBlank} disabled={busy} className="text-xs px-3 py-1 h-auto btn-secondary">
                {t('whitelabel.logo.blank', 'Blank')}
              </Button>
            </div>
            <button
              type="button"
              onClick={handleSetBlank}
              disabled={busy}
              className="text-[10px] text-white/80 underline hover:text-white disabled:opacity-40"
            >
              {t('route.whitelabel.setBlank', 'Load Blank Logo')}
            </button>
          </div>
        )}

        {busy && !file && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-md">
            <span className="animate-pulse text-xs text-gray-600">{t('common.loading', 'Loading')}...</span>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3">
        {!file && !currentLogoB64 && (
          <button
            type="button"
            onClick={handleSetBlank}
            className="text-xs text-primary-600 underline disabled:opacity-50"
            disabled={busy}
          >
            {t('route.whitelabel.setBlank', 'Load Blank Logo')}
          </button>
        )}
      </div>
    </div>
  );
};
